import React, { useState, createContext, useContext } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Navbar from "./components/Navbar";
import HeroSection from "./components/HeroSection";
import PressLogos from "./components/PressLogos";
import ServiceCategories from "./components/ServiceCategories";
import MostPopular from "./components/MostPopular";
import Testimonials from "./components/Testimonials";
import HowItWorks from "./components/HowItWorks";
import ProCTA from "./components/ProCTA";
import Footer from "./components/Footer";
import RequestModal from "./components/RequestModal";
import LoginPage from "./components/LoginPage";
import AuthCallback from "./components/AuthCallback";
import ServiceWizard from "./components/ServiceWizard";
import ResultsPage from "./components/ResultsPage";
import RegisterPage from "./components/RegisterPage";
import RegisterProfessional from "./components/RegisterProfessional";
import DashboardLayout from "./components/dashboard/DashboardLayout";
import AvailableLeads from "./components/dashboard/AvailableLeads";
import MyLeads from "./components/dashboard/MyLeads";
import Wallet from "./components/dashboard/Wallet";
import Performance from "./components/dashboard/Performance";
import ProfileCompletion from "./components/dashboard/ProfileCompletion";
import CustomerDashboard from "./components/customer/CustomerDashboard";
import MyRequests from "./components/customer/MyRequests";
import AdminProfessionals from "./components/admin/AdminProfessionals";
import AdminLeadReports from "./components/admin/AdminLeadReports";
import AdminStatus from "./components/admin/AdminStatus";
import AdminVerifications from "./components/admin/AdminVerifications";
import { TermsOfService, PrivacyPolicy } from "./components/LegalPages";
import CookieConsent from "./components/CookieConsent";
import ClaimPage from "./components/ClaimPage";
import { AuthProvider } from "./contexts/AuthContext";

// Modal context
export const ModalContext = createContext();
export const useModal = () => useContext(ModalContext);

const Home = () => {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <HeroSection />
      <PressLogos />
      <ServiceCategories />
      <MostPopular />
      <Testimonials />
      <HowItWorks />
      <ProCTA />
      <Footer />
    </div>
  );
};

function AppRouter() {
  const location = useLocation();

  // CRITICAL: Check URL fragment for session_id synchronously during render
  // This prevents race conditions by processing new session_id FIRST
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/register-professional" element={<RegisterProfessional />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/service/:slug" element={<ServiceWizard />} />
      <Route path="/results/:slug" element={<ResultsPage />} />
      
      {/* Professional Dashboard */}
      <Route path="/dashboard/:proId" element={<DashboardLayout />}>
        <Route path="leads" element={<AvailableLeads />} />
        <Route path="my-leads" element={<MyLeads />} />
        <Route path="wallet" element={<Wallet />} />
        <Route path="performance" element={<Performance />} />
        <Route path="complete-profile" element={<ProfileCompletion />} />
      </Route>

      {/* Customer Dashboard */}
      <Route path="/dashboard/customer" element={<CustomerDashboard />}>
        <Route path="requests" element={<MyRequests />} />
      </Route>

      {/* Admin Console */}
      <Route path="/admin/professionals" element={<AdminProfessionals />} />
      <Route path="/admin/lead-reports" element={<AdminLeadReports />} />
      <Route path="/admin/status" element={<AdminStatus />} />
      <Route path="/admin/verifications" element={<AdminVerifications />} />
      {/* Legal */}
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />

      {/* Professional invitation claim */}
      <Route path="/claim/:token" element={<ClaimPage />} />

      <Route path="/" element={<Home />} />
    </Routes>
  );
}

function App() {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalService, setModalService] = useState('');

  const openRequestModal = (serviceName = '') => {
    setModalService(serviceName);
    setModalOpen(true);
  };

  const closeRequestModal = () => {
    setModalOpen(false);
    setModalService('');
  };

  return (
    <AuthProvider>
      <ModalContext.Provider value={{ openRequestModal, closeRequestModal }}>
        <div className="App">
          <BrowserRouter>
            <AppRouter />
            <CookieConsent />
          </BrowserRouter>
          <RequestModal
            isOpen={modalOpen}
            onClose={closeRequestModal}
            serviceName={modalService}
          />
        </div>
      </ModalContext.Provider>
    </AuthProvider>
  );
}

export default App;
